import { z } from "zod";

import { CATEGORIAS_META } from "./enums";
import type { CategoriaMeta, TipoEnvio } from "./enums";

/**
 * Economia com os números não oficiais (D15). Valores monetários SEMPRE em
 * centavos de real (inteiros); preços da Meta em USD (string decimal).
 */

/** Motivos gravados no `custoSnapshot` da mensagem. */
export const MOTIVOS_ECONOMIA = [
  "fora_janela",
  "janela_aberta",
  "lead_janela_oficial",
  "sem_tabela_preco",
] as const;
export type MotivoEconomia = (typeof MOTIVOS_ECONOMIA)[number];

/** Snapshot IMUTÁVEL gravado em `Mensagem.custoSnapshot` no momento do envio. */
export interface CustoSnapshot {
  /** Preços unitários usados (USD). Null quando o motivo zera a economia. */
  precoMarketingUsd: number | null;
  precoUtilityUsd: number | null;
  /** Câmbio USD→BRL usado. */
  cambio: number | null;
  /** DDI do destinatário (null se o telefone não é E.164 reconhecível). */
  ddi: string | null;
  /** Linha da tabela usada: o próprio DDI ou "*" (Demais países). */
  ddiTabela?: string | null;
  tabelaVersao: number | null;
  motivo: MotivoEconomia;
  /** Dia (America/Sao_Paulo) em que entrou no agregado, `YYYY-MM-DD`. */
  dia?: string;
  /** Preenchido quando a mensagem falhou e foi estornada do agregado. */
  estornadoEm?: string;
}

// ─── Consulta ────────────────────────────────────────────────────────────────

export const PERIODOS_ECONOMIA = ["mes", "30d", "inicio"] as const;
export type PeriodoEconomia = (typeof PERIODOS_ECONOMIA)[number];

export const zConsultaEconomia = z.object({
  periodo: z.enum(PERIODOS_ECONOMIA).default("mes"),
  /** publicId do número (opcional). */
  numeroId: z.string().trim().min(1).max(64).optional(),
});
export type ConsultaEconomia = z.infer<typeof zConsultaEconomia>;

export interface ValoresEconomia {
  marketingCentavos: number;
  utilityCentavos: number;
}

export interface EconomiaPorTipo extends ValoresEconomia {
  tipoEnvio: TipoEnvio;
  qtd: number;
}

export interface EconomiaPorCampanha extends ValoresEconomia {
  campanhaId: string; // publicId
  nome: string;
  qtd: number;
}

export interface EconomiaPorAnuncio extends ValoresEconomia {
  anuncioId: string;
  titulo: string | null;
  qtd: number;
}

export interface GastoOficialEstimado {
  /** Soma estimada do que a Meta cobra pelos templates enviados, em centavos. */
  centavos: number;
  /** Templates considerados. */
  qtd: number;
  porCategoria: Array<{ categoria: CategoriaMeta; qtd: number; centavos: number }>;
  /** Sempre true: é estimativa pela tabela pública, não a fatura da Meta. */
  estimado: true;
}

export interface ResumoEconomia {
  periodo: { inicio: string; fim: string }; // YYYY-MM-DD (America/Sao_Paulo)
  /** publicId do número filtrado, se houve filtro. */
  numeroId: string | null;
  /** Mensagens que geraram economia (fora da janela) e o total nas duas faixas. */
  totais: ValoresEconomia & { qtd: number };
  porTipoEnvio: EconomiaPorTipo[];
  gastoOficial: GastoOficialEstimado;
  porOrigemLead: { campanhas: EconomiaPorCampanha[]; anuncios: EconomiaPorAnuncio[] };
}

export interface ComoCalculamos {
  brasil: { ddi: "55"; marketingUsd: string; utilityUsd: string };
  cambio: { usdBrl: string; vigenteDe: string; fonte: string };
  tabela: { versao: number; vigenteDe: string; diasDesdeAtualizacao: number };
  /** true quando a tabela tem mais de 90 dias sem atualização. */
  tabelaDesatualizada: boolean;
  aviso: string | null;
}

// ─── Admin (SUPERADMIN) ──────────────────────────────────────────────────────

/** DDI numérico (1 a 3 dígitos) ou "*" (Demais países). */
export const zDdiTabela = z.union([
  z.literal("*"),
  z.string().regex(/^[1-9][0-9]{0,2}$/, "DDI inválido (1 a 3 dígitos, sem zero à esquerda)."),
]);

const zPrecoUsd = z
  .union([z.number(), z.string()])
  .transform((v) => String(v).trim().replace(",", "."))
  .refine((v) => /^\d{1,6}(\.\d{1,6})?$/.test(v), "Preço USD inválido (até 6 casas decimais).")
  .refine((v) => Number(v) > 0, "O preço deve ser maior que zero.");

export const zCriarVigenciaPrecos = z.object({
  itens: z
    .array(
      z.object({
        ddi: zDdiTabela,
        categoria: z.enum(CATEGORIAS_META),
        precoUsd: zPrecoUsd,
      }),
    )
    .min(1)
    .max(200),
  /** Padrão: agora. Não pode ser anterior à vigência atual do mesmo DDI/categoria. */
  vigenteDe: z.coerce.date().optional(),
});
export type CriarVigenciaPrecos = z.infer<typeof zCriarVigenciaPrecos>;

export const zConsultaHistoricoPrecos = z.object({
  ddi: zDdiTabela.optional(),
  categoria: z.enum(CATEGORIAS_META).optional(),
});
export type ConsultaHistoricoPrecos = z.infer<typeof zConsultaHistoricoPrecos>;

export const zDefinirCambio = z.object({
  usdBrl: z
    .union([z.number(), z.string()])
    .transform((v) => String(v).trim().replace(",", "."))
    .refine((v) => /^\d{1,8}(\.\d{1,4})?$/.test(v), "Câmbio inválido (até 4 casas decimais).")
    .refine((v) => Number(v) > 0, "O câmbio deve ser maior que zero."),
  fonte: z.string().trim().min(1).max(60).default("manual"),
});
export type DefinirCambio = z.infer<typeof zDefinirCambio>;

export interface PrecoMetaDto {
  ddi: string;
  /** "Demais países" quando ddi = "*". */
  rotulo: string;
  categoria: CategoriaMeta;
  precoUsd: string;
  versao: number;
  vigenteDe: string;
}

export interface CambioDto {
  usdBrl: string;
  fonte: string;
  vigenteDe: string;
}
